import { IMapViewPointsResponse } from './../../api/mapView/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getFullMapViewThunk, getMapViewFilteredPointsThunk, getMapViewPolygonsThunk, getAnalogueMapViewFilteredPointsThunk } from './thunk';
import { IMapViewState } from './types';

const initialState: IMapViewState = {
  isLoading: false,
  isFullMapLoading: false,
  isFetched: false,
  isMapViewFullDataFetched: false,
  error: null,
  mapView: [],
  mapViewPoints: [[]],
  mapViewFullData: {
    sources: [],
    transports: [],
    storages: [],
    ccsProjects: [],
    blocks: [],
    licences: [],
    windfarms: [],
    userDataLegends: [],
  },
  mapViewData: {
    categories: [],
    sources: [],
    transports: [],
    storages: [],
    ccsProjects: [],
    blocks: [],
    licences: [],
    windfarms: [],
    userDataLegends: [],
  },
  mapViewPolygons: {
    sources: [],
    transports: [],
    storages: [],
    ccsProjects: [],
    blocks: [],
    licences: [],
    windfarms: [],
    userDataLegends: [],
  },
  selectedPoint: undefined,
  searchZoomCoordinates: null,
}

export const mapViewSlice = createSlice({
    name: 'mapView',
    initialState,
    reducers: {
        setSelectedPoint: (state, action) => {
            state.selectedPoint = action.payload;
        },
        changeIsFetchedStatus: (state, action) => {
            state.isFetched = action.payload
        },
        resetIsMapViewFullDataFetched: (state, action) => {
            state.isMapViewFullDataFetched = action.payload
        },
        changeMapLoadingStatus: (state, action: PayloadAction<{ status: boolean}>) => {
            state.isLoading = action.payload.status
          },
        clearMapViewPolygons: (state) => {
            state.mapViewPolygons = {
                sources: [],
                transports: [],
                storages: [],
                ccsProjects: [],
                blocks: [],
                licences: [],
                windfarms: [],
                userDataLegends: [],
            };
        },
        searchZoomToCoordinates: (state, action: PayloadAction<{ longitude: number; latitude: number; zoom?: number }>) => {
            state.searchZoomCoordinates = action.payload;
        },
        clearSearchZoom: (state) => {
            state.searchZoomCoordinates = null;
        },
          
    },
    extraReducers: {
        [getMapViewFilteredPointsThunk.pending.type]: (state) => {
            state.isLoading = true;
            state.error = null;
        },

        [getMapViewFilteredPointsThunk.fulfilled.type]: (state, action: PayloadAction<{response: IMapViewPointsResponse, mapViewPoints: IMapViewState['mapViewPoints'], mapViewData: IMapViewState['mapViewData']  }>) => {
            const { mapViewData } = action.payload;
            state.mapViewData = {
                ...state.mapViewData,
                categories: [...(mapViewData.categories || state.mapViewData.categories || [])],
                sources: mapViewData.sources  || [],
                transports: mapViewData.transports || [],
                storages: mapViewData.storages || [],
                ccsProjects: mapViewData.ccsProjects  || [],
                blocks: mapViewData.blocks  || [],
                licences: mapViewData.licences || [],
                windfarms: mapViewData.windfarms  || [],
                userDataLegends: mapViewData.userDataLegends  || [],
            };
            state.isLoading = false;
            state.isFetched = true;
            state.error = null;
        },

        [getMapViewFilteredPointsThunk.rejected.type]: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.isLoading = false;
            state.isFetched = true
        },

        [getAnalogueMapViewFilteredPointsThunk.pending.type]: (state) => {
            state.isLoading = true;
            state.error = null;
        },

        [getAnalogueMapViewFilteredPointsThunk.fulfilled.type]: (state, action: PayloadAction<{response: IMapViewPointsResponse, mapViewPoints: IMapViewState['mapViewPoints'], mapViewData: IMapViewState['mapViewData']  }>) => {
            const { mapViewData } = action.payload;
            state.mapViewData = {
                ...state.mapViewData,
                categories: [...(mapViewData.categories || state.mapViewData.categories || [])],
                sources: mapViewData.sources  || [],
                transports: mapViewData.transports || [],
                storages: mapViewData.storages || [],
                ccsProjects: mapViewData.ccsProjects  || [],
                blocks: mapViewData.blocks  || [],
                licences: mapViewData.licences || [],
                windfarms: mapViewData.windfarms  || [],
                userDataLegends: mapViewData.userDataLegends  || [],
            };
            state.isLoading = false;
            state.isFetched = true;
            state.error = null;
        },

        [getAnalogueMapViewFilteredPointsThunk.rejected.type]: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.isLoading = false;
            state.isFetched = true
        },


        [getMapViewPolygonsThunk.pending.type]: (state) => {
            state.isLoading = true;
        },

        [getMapViewPolygonsThunk.fulfilled.type]: (state, action: PayloadAction<{response: IMapViewPointsResponse, mapViewPoints: IMapViewState['mapViewPoints'], mapViewData: IMapViewState['mapViewData']  }>) => {
            const { mapViewData } = action.payload;
            
            state.mapViewPolygons = {
                ...state.mapViewPolygons,
                ...mapViewData,
                categories: [...(mapViewData.categories || [])],
                sources: mapViewData.sources?.length ? mapViewData.sources : [],
                transports: mapViewData.transports?.length ? mapViewData.transports : [],
                storages: mapViewData.storages?.length ? mapViewData.storages : [],
                ccsProjects: mapViewData.ccsProjects?.length ? mapViewData.ccsProjects : [],
                blocks: mapViewData.blocks?.length ? mapViewData.blocks : [],
                licences: mapViewData.licences?.length ? mapViewData.licences : [],
                windfarms: mapViewData.windfarms?.length ? mapViewData.windfarms : [],
                userDataLegends: mapViewData.userDataLegends?.length ? mapViewData.userDataLegends : [],
            };
            
            state.isLoading = false;
        },

        [getMapViewPolygonsThunk.rejected.type]: (state, action: PayloadAction<string>) => {
            state.isFullMapLoading = false;
            state.error = action.payload;
        },

        [getFullMapViewThunk.pending.type]: (state) => {
            state.isFullMapLoading = true;
        },

        [getFullMapViewThunk.fulfilled.type]: (state, action: PayloadAction<{response: IMapViewPointsResponse, mapViewPoints: IMapViewState['mapViewPoints'], mapViewData: IMapViewState['mapViewData']  }>) => {
            const { mapViewData } = action.payload;
            
            state.mapViewFullData = {
                ...state.mapViewFullData,
                ...mapViewData,
                categories: [...(mapViewData.categories || [])],
                sources: mapViewData.sources?.length ? mapViewData.sources : [],
                transports: mapViewData.transports?.length ? mapViewData.transports : [],
                storages: mapViewData.storages?.length ? mapViewData.storages : [],
                ccsProjects: mapViewData.ccsProjects?.length ? mapViewData.ccsProjects : [],
                blocks: mapViewData.blocks?.length ? mapViewData.blocks : [],
                licences: mapViewData.licences?.length ? mapViewData.licences : [],
                windfarms: mapViewData.windfarms?.length ? mapViewData.windfarms : [],
                userDataLegends: mapViewData.userDataLegends?.length ? mapViewData.userDataLegends : [],
            };
            
            state.isLoading = false;
            state.isFullMapLoading = false;
            state.isMapViewFullDataFetched = true;
        },

        [getFullMapViewThunk.rejected.type]: (state, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },
    },
});

export default mapViewSlice.reducer;

